import type { StairLink } from './nav-graph';
import { NavGrid } from './nav-grid';
import { SiteModelLevel } from './site-model.types';

/**
 * Bloc `navigation` v1 du GLB de site (`scenes[0].extras.navigation`, [[contrat-glb-site]],
 * amendement 1 d'ADR-017) : murs, ouvertures, escaliers et pièces décrits par l'export, au lieu
 * d'être devinés dans la géométrie. Repère du plan : x, y à l'horizontale, z vers le haut, en mètres.
 *
 * Bloc facultatif : absent ou invalide, le front garde ses heuristiques (`nav-grid.ts`).
 */
type XY = [number, number];
type XYZ = [number, number, number];

export interface NavigationV1 {
  version: 1;
  levels: { id: string; height: number }[];
  ground: { level: string; z: number } | null;
  rooms: { id: string; polygon: XY[] }[];
  walls: { level: string; a: XY; b: XY; thickness: number; kind: WallKind }[];
  openings: { id: string; level: string; kind: OpeningKind; a: XY; b: XY; between: [string, string] }[];
  stairs: { id: string; from: string; to: string; width: number; line: XYZ[] }[];
}

const WALL_KINDS = ['wall', 'glass', 'railing', 'partition'] as const;
const OPENING_KINDS = ['door', 'double_door', 'passage'] as const;
type WallKind = (typeof WALL_KINDS)[number];
type OpeningKind = (typeof OPENING_KINDS)[number];
/** Tolérance entre les bouts d'une ligne de marche et les planchers qu'elle relie (m). */
const STAIR_END_M = 0.3;
/** Demi-profondeur percée de part et d'autre de l'axe d'une ouverture (m), au moins. */
const OPENING_HALF_DEPTH_M = 0.3;

/**
 * Lit et contrôle le bloc `navigation` de `extras` (métadonnées de scène du GLB). `roomLevels` donne
 * l'étage de chaque pièce (`extras.rooms[].level`). Sans bloc : `{ nav: null, issues: [] }`. Bloc
 * invalide : `nav` à `null` et les chemins fautifs dans `issues` (le bloc entier est ignoré).
 */
export function parseNavigation(
  extras: unknown,
  levels: SiteModelLevel[],
  roomLevels: Record<string, string>
): { nav: NavigationV1 | null; issues: string[] } {
  const raw = isObject(extras) ? extras['navigation'] : undefined;
  if (raw === undefined || raw === null) {
    return { nav: null, issues: [] };
  }
  const issues: string[] = [];
  const bad = (path: string, why: string) => issues.push(`navigation.${path} : ${why}`);
  if (!isObject(raw)) {
    bad('', "n'est pas un objet");
    return { nav: null, issues };
  }
  if (raw['version'] !== 1) {
    bad('version', `version inconnue (${String(raw['version'])}), lue seulement en v1`);
    return { nav: null, issues };
  }
  const levelIds = new Set(levels.map(l => l.id));
  const roomIds = new Set(Object.keys(roomLevels));
  const list = (key: string) => {
    const v = raw[key];
    if (v === undefined) return [];
    if (!Array.isArray(v)) {
      bad(key, "n'est pas une liste");
      return [];
    }
    return v;
  };
  const unique = (key: string, ids: unknown[]) => {
    const seen = new Set<unknown>();
    ids.forEach((id, i) => {
      if (seen.has(id)) bad(`${key}[${i}].id`, `id en double (${String(id)})`);
      seen.add(id);
    });
  };

  const navLevels = list('levels').map((l, i) => {
    if (!isObject(l) || !levelIds.has(l['id'] as string)) bad(`levels[${i}].id`, 'étage inconnu');
    if (!isObject(l) || !(Number(l['height']) > 0)) bad(`levels[${i}].height`, 'hauteur ≤ 0 ou absente');
    return { id: String(isObject(l) ? l['id'] : ''), height: Number(isObject(l) ? l['height'] : 0) };
  });

  let ground: NavigationV1['ground'] = null;
  if (raw['ground'] !== undefined) {
    const g = raw['ground'];
    if (!isObject(g) || !levelIds.has(g['level'] as string) || !Number.isFinite(g['z'])) {
      bad('ground', 'étage inconnu ou altitude absente');
    } else {
      ground = { level: g['level'] as string, z: g['z'] as number };
    }
  }

  const rooms = list('rooms').map((r, i) => {
    const id = isObject(r) ? r['id'] : undefined;
    if (!roomIds.has(id as string)) bad(`rooms[${i}].id`, "pièce absente d'extras.rooms");
    const polygon = isObject(r) ? points(r['polygon'], 2) : null;
    if (!polygon || polygon.length < 3) bad(`rooms[${i}].polygon`, 'au moins 3 points [x, y]');
    return { id: String(id), polygon: (polygon ?? []) as XY[] };
  });
  unique(
    'rooms',
    rooms.map(r => r.id)
  );

  const walls = list('walls').map((w, i) => {
    const o = isObject(w) ? w : {};
    const a = point(o['a'], 2);
    const b = point(o['b'], 2);
    if (!levelIds.has(o['level'] as string)) bad(`walls[${i}].level`, 'étage inconnu');
    if (!a || !b) bad(`walls[${i}]`, 'a et b doivent être des points [x, y]');
    if (!(Number(o['thickness']) > 0)) bad(`walls[${i}].thickness`, 'épaisseur ≤ 0 ou absente');
    if (!WALL_KINDS.includes(o['kind'] as WallKind)) bad(`walls[${i}].kind`, `attendu ${WALL_KINDS.join(', ')}`);
    return {
      level: String(o['level']),
      a: (a ?? [0, 0]) as XY,
      b: (b ?? [0, 0]) as XY,
      thickness: Number(o['thickness']),
      kind: o['kind'] as WallKind,
    };
  });

  const openings = list('openings').map((w, i) => {
    const o = isObject(w) ? w : {};
    const a = point(o['a'], 2);
    const b = point(o['b'], 2);
    const between = o['between'];
    if (!levelIds.has(o['level'] as string)) bad(`openings[${i}].level`, 'étage inconnu');
    if (!a || !b) bad(`openings[${i}]`, 'a et b doivent être des points [x, y]');
    if (!OPENING_KINDS.includes(o['kind'] as OpeningKind)) {
      bad(`openings[${i}].kind`, `attendu ${OPENING_KINDS.join(', ')}`);
    }
    if (
      !Array.isArray(between) ||
      between.length !== 2 ||
      !between.every(x => x === 'outside' || roomIds.has(x as string))
    ) {
      bad(`openings[${i}].between`, 'deux id de pièce, ou "outside"');
    }
    return {
      id: String(o['id']),
      level: String(o['level']),
      kind: o['kind'] as OpeningKind,
      a: (a ?? [0, 0]) as XY,
      b: (b ?? [0, 0]) as XY,
      between: (Array.isArray(between) ? between : ['', '']) as [string, string],
    };
  });
  unique(
    'openings',
    openings.map(o => o.id)
  );

  const z = (id: unknown) => levels.find(l => l.id === id)?.z;
  const stairs = list('stairs').map((s, i) => {
    const o = isObject(s) ? s : {};
    const line = points(o['line'], 3) as XYZ[] | null;
    const from = z(o['from']);
    const to = z(o['to']);
    if (from === undefined || to === undefined || to <= from)
      bad(`stairs[${i}]`, 'from / to : deux étages, du bas vers le haut');
    if (!(Number(o['width']) > 0)) bad(`stairs[${i}].width`, 'largeur ≤ 0 ou absente');
    if (
      !line ||
      line.length < 2 ||
      line.some((p, k) => k > 0 && p[2] < line[k - 1][2]) ||
      (from !== undefined && Math.abs(line[0][2] - from) > STAIR_END_M) ||
      (to !== undefined && Math.abs(line[line.length - 1][2] - to) > STAIR_END_M)
    ) {
      bad(`stairs[${i}].line`, `≥ 2 points [x, y, z], z croissant, bouts à ± ${STAIR_END_M} m des planchers`);
    }
    return {
      id: String(o['id']),
      from: String(o['from']),
      to: String(o['to']),
      width: Number(o['width']),
      line: line ?? [],
    };
  });
  unique(
    'stairs',
    stairs.map(s => s.id)
  );

  if (issues.length) {
    return { nav: null, issues };
  }
  return { nav: { version: 1, levels: navLevels, ground, rooms, walls, openings, stairs }, issues };
}

/**
 * Grille de circulation d'un étage tirée du bloc `navigation` : pièces de l'étage = plancher, murs
 * épais = obstacles, ouvertures percées sur toute l'épaisseur, sol extérieur au plancher `ground`.
 * Grille en repère three.js (X = x, Z = −y).
 */
export function navGridFromNavigation(
  nav: NavigationV1,
  levelId: string,
  opts: { clearanceM: number; roomLevels: Record<string, string>; outdoorM?: number; cellM?: number }
): NavGrid {
  const cell = opts.cellM ?? 0.1;
  const t = (p: XY) => ({ x: p[0], z: -p[1] });
  const rooms = nav.rooms.filter(r => opts.roomLevels[r.id] === levelId).map(r => r.polygon.map(t));
  const walls = nav.walls.filter(w => w.level === levelId).map(w => ({ a: t(w.a), b: t(w.b), half: w.thickness / 2 }));
  // Percée au moins sur l'épaisseur du mur le plus épais de l'étage, plus 10 cm de chaque côté.
  const half = Math.max(OPENING_HALF_DEPTH_M, ...walls.map(w => w.half + 0.1));
  const openings = nav.openings.filter(o => o.level === levelId).map(o => ({ a: t(o.a), b: t(o.b), half }));
  const outdoor = nav.ground?.level === levelId ? (opts.outdoorM ?? 0) : 0;

  const pts = [...rooms.flat(), ...walls.flatMap(w => [w.a, w.b])];
  if (!pts.length) {
    return NavGrid.fromMasks(
      {
        x0: 0,
        z0: 0,
        cell,
        nx: 1,
        nz: 1,
        floor: new Uint8Array(1),
        wall: new Uint8Array(1),
        opening: new Uint8Array(1),
      },
      opts.clearanceM,
      false
    );
  }
  const minX = Math.min(...pts.map(p => p.x)) - outdoor - cell;
  const maxX = Math.max(...pts.map(p => p.x)) + outdoor + cell;
  const minZ = Math.min(...pts.map(p => p.z)) - outdoor - cell;
  const maxZ = Math.max(...pts.map(p => p.z)) + outdoor + cell;
  const nx = Math.ceil((maxX - minX) / cell) + 1;
  const nz = Math.ceil((maxZ - minZ) / cell) + 1;
  const floor = new Uint8Array(nx * nz);
  const wall = new Uint8Array(nx * nz);
  const opening = new Uint8Array(nx * nz);
  for (let iz = 0; iz < nz; iz++) {
    for (let ix = 0; ix < nx; ix++) {
      const c = { x: minX + (ix + 0.5) * cell, z: minZ + (iz + 0.5) * cell };
      const i = iz * nx + ix;
      if (rooms.some(poly => inside(poly, c))) floor[i] = 1;
      if (walls.some(w => segmentDistance(c, w.a, w.b) <= w.half + cell / 2)) wall[i] = 1;
      if (openings.some(o => inRect(c, o.a, o.b, o.half))) opening[i] = 1;
    }
  }
  return NavGrid.fromMasks({ x0: minX, z0: minZ, cell, nx, nz, floor, wall, opening }, opts.clearanceM, outdoor > 0);
}

/** Lignes de marche des escaliers déclarés, en repère three.js, pour le graphe des étages. */
export function stairsFromNavigation(nav: NavigationV1, levels: SiteModelLevel[]): StairLink[] {
  const z = (id: string) => levels.find(l => l.id === id)?.z as number;
  return nav.stairs.map(s => {
    const line = s.line.map(([x, y, h]) => ({ x, y: h, z: -y }));
    line[0] = { ...line[0], y: z(s.from) };
    line[line.length - 1] = { ...line[line.length - 1], y: z(s.to) };
    return { lower: z(s.from), upper: z(s.to), line };
  });
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function point(v: unknown, n: number): number[] | null {
  return Array.isArray(v) && v.length === n && v.every(x => Number.isFinite(x)) ? (v as number[]) : null;
}

function points(v: unknown, n: number): number[][] | null {
  if (!Array.isArray(v)) return null;
  const out = v.map(p => point(p, n));
  return out.every(p => p !== null) ? (out as number[][]) : null;
}

function inside(poly: { x: number; z: number }[], p: { x: number; z: number }): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (a.z > p.z !== b.z > p.z && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) hit = !hit;
  }
  return hit;
}

function segmentDistance(
  p: { x: number; z: number },
  a: { x: number; z: number },
  b: { x: number; z: number }
): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

function inRect(
  p: { x: number; z: number },
  a: { x: number; z: number },
  b: { x: number; z: number },
  half: number
): boolean {
  const len = Math.hypot(b.x - a.x, b.z - a.z);
  if (!len) return false;
  const ux = (b.x - a.x) / len;
  const uz = (b.z - a.z) / len;
  const along = (p.x - a.x) * ux + (p.z - a.z) * uz;
  const across = -(p.x - a.x) * uz + (p.z - a.z) * ux;
  return along >= 0 && along <= len && Math.abs(across) <= half;
}
